import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  HelpBanner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@betstarters/ui'
import { Ban, Pencil, Plus, RotateCcw } from 'lucide-react'
import { api } from '../api'
import { useCapabilities } from '../hooks/useCapabilities'
import { messageOf } from '../lib/apiError'
import {
  CatalogToolbar,
  ErrorBanner,
  PageHeading,
  Pagination,
  StatusBadge,
} from '../components/catalog'
import {
  JurisdictionForm,
  emptyJurisdictionForm,
  type JurisdictionFormValue,
} from '../components/geoForms'

export interface Jurisdiction {
  jurisdictionId: number
  jurisdiction: string
  isDisabledByPlatform: boolean
  creationDate: string
  lastModificationDate: string | null
  countryCount: number
  activeCountryCount: number
}

interface JurisdictionPage {
  jurisdictions: Jurisdiction[]
  total: number
  offset: number
  limit: number
}

const PAGE_SIZE = 50

/**
 * JurisdictionsPage is the licensing jurisdiction catalogue —
 * [Catalog].[Geographic.Jurisdiction] in the BsConnekto database.
 *
 * The countries column shows active over total, because retiring a jurisdiction
 * leaves its links alone and a link can be disabled on its own: those two
 * numbers are what tell an operator what is actually still covered.
 */
export function JurisdictionsPage() {
  const { t } = useTranslation('app')
  const qc = useQueryClient()
  const { has } = useCapabilities()
  const canWrite = has('catalog:write')

  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [includeDisabled, setIncludeDisabled] = useState(true)
  const [offset, setOffset] = useState(0)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<Jurisdiction | null>(null)
  const [form, setForm] = useState<JurisdictionFormValue>(emptyJurisdictionForm)
  const [error, setError] = useState('')

  const params = { search, includeDisabled: String(includeDisabled), offset, limit: PAGE_SIZE }

  const { data, isLoading } = useQuery({
    queryKey: ['jurisdictions', params],
    queryFn: async (): Promise<JurisdictionPage> => {
      const res = await api.get('/jurisdictions', { params })
      return res.data as JurisdictionPage
    },
  })

  const invalidate = () => qc.invalidateQueries({ queryKey: ['jurisdictions'] })

  const openCreate = () => {
    setEditing(null)
    setForm(emptyJurisdictionForm)
    setError('')
    setDialogOpen(true)
  }

  const openEdit = (j: Jurisdiction) => {
    setEditing(j)
    setForm({ jurisdiction: j.jurisdiction })
    setError('')
    setDialogOpen(true)
  }

  const save = useMutation({
    mutationFn: () => {
      const body = {
        jurisdiction: form.jurisdiction.trim(),
        isDisabledByPlatform: editing?.isDisabledByPlatform ?? false,
      }
      return editing
        ? api.put(`/jurisdictions/${editing.jurisdictionId}`, body)
        : api.post('/jurisdictions', body)
    },
    onSuccess: () => {
      setDialogOpen(false)
      setError('')
      invalidate()
    },
    onError: (err) => setError(messageOf(err, t('jurisdictions.errors.save'))),
  })

  const toggle = useMutation({
    mutationFn: (j: Jurisdiction) =>
      j.isDisabledByPlatform
        ? api.post(`/jurisdictions/${j.jurisdictionId}/restore`)
        : api.delete(`/jurisdictions/${j.jurisdictionId}`),
    onSuccess: () => {
      setError('')
      invalidate()
    },
    onError: (err) => setError(messageOf(err, t('jurisdictions.errors.save'))),
  })

  const jurisdictions = data?.jurisdictions ?? []
  const total = data?.total ?? 0

  return (
    <div>
      <PageHeading
        title={t('jurisdictions.title')}
        subtitle={t('jurisdictions.subtitle')}
        action={
          canWrite && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger
                render={
                  <Button onClick={openCreate}>
                    <Plus />
                    {t('jurisdictions.newJurisdiction')}
                  </Button>
                }
              />
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>
                    {editing ? t('jurisdictions.editJurisdiction') : t('jurisdictions.newJurisdiction')}
                  </DialogTitle>
                </DialogHeader>
                <JurisdictionForm
                  value={form}
                  onChange={setForm}
                  onSubmit={() => save.mutate()}
                  onCancel={() => setDialogOpen(false)}
                  pending={save.isPending}
                  error={error}
                  submitLabel={editing ? t('common.save') : t('common.create')}
                />
              </DialogContent>
            </Dialog>
          )
        }
      />

      <HelpBanner storageKey="jurisdictions">
        <p>{t('jurisdictions.help.line1')}</p>
        <p>{t('jurisdictions.help.line2')}</p>
      </HelpBanner>

      <CatalogToolbar
        searchInput={searchInput}
        onSearchInput={setSearchInput}
        onSubmitSearch={() => {
          setSearch(searchInput)
          setOffset(0)
        }}
        searchPlaceholder={t('jurisdictions.searchPlaceholder')}
        includeDisabled={includeDisabled}
        onToggleDisabled={() => {
          setIncludeDisabled(!includeDisabled)
          setOffset(0)
        }}
        filtered={search !== '' || !includeDisabled}
        onClear={() => {
          setSearch('')
          setSearchInput('')
          setIncludeDisabled(true)
          setOffset(0)
        }}
      />

      {!dialogOpen && <ErrorBanner message={error} />}

      {isLoading ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : (
        <>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="w-16">{t('jurisdictions.columns.id')}</TableHead>
                  <TableHead>{t('jurisdictions.columns.name')}</TableHead>
                  <TableHead className="w-40">{t('jurisdictions.columns.countries')}</TableHead>
                  <TableHead className="w-32">{t('common.columns.status')}</TableHead>
                  {canWrite && (
                    <TableHead className="w-28 text-right">{t('common.columns.actions')}</TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {jurisdictions.map((j) => (
                  <TableRow key={j.jurisdictionId}>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {j.jurisdictionId}
                    </TableCell>
                    <TableCell className="font-medium">
                      <Link
                        to={`/jurisdictions/${j.jurisdictionId}`}
                        className="text-primary underline underline-offset-2"
                      >
                        {j.jurisdiction}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Link
                        to={`/jurisdictions/${j.jurisdictionId}?tab=countries`}
                        className="text-primary underline underline-offset-2"
                      >
                        {t('jurisdictions.countriesValue', {
                          active: j.activeCountryCount,
                          total: j.countryCount,
                        })}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <StatusBadge disabled={j.isDisabledByPlatform} />
                    </TableCell>
                    {canWrite && (
                      <TableCell>
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            title={t('common.edit')}
                            onClick={() => openEdit(j)}
                          >
                            <Pencil />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            title={j.isDisabledByPlatform ? t('common.restore') : t('common.retire')}
                            disabled={toggle.isPending}
                            onClick={() => toggle.mutate(j)}
                          >
                            {j.isDisabledByPlatform ? <RotateCcw /> : <Ban />}
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {jurisdictions.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={canWrite ? 5 : 4}
                      className="py-8 text-center text-muted-foreground"
                    >
                      {t('jurisdictions.empty')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          <Pagination
            total={total}
            offset={offset}
            pageSize={PAGE_SIZE}
            onOffset={setOffset}
            label={t('jurisdictions.total', { count: total })}
          />
        </>
      )}
    </div>
  )
}
